import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { fetchTransferHistory } from '../../utils/studentsApi';
import { showError } from '../../components/CustomAlert';
import { FaHistory, FaSearch, FaArrowRight, FaFilter } from 'react-icons/fa';
import { format } from 'date-fns';

const Container = styled.div`
  padding: 30px;
  min-height: 100vh;
  background: #f8f9fa;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 0px;
`;

const Subtitle = styled.p`
  color: #6c757d;
  font-size: 1rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  padding: 30px;
  margin-bottom: 30px;
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  table-layout: fixed;
`;

const Th = styled.th`
  padding: 15px 12px;
  text-align: left;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
`;

const Td = styled.td`
  padding: 15px 12px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
  font-size: 0.9rem;
`;

const Tr = styled.tr`
  transition: background 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }
`;

const Badge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  background: ${props => {
    switch (props.type) {
      case 'Course Completion': return '#d4edda';
      case 'Schedule Change': return '#fff3cd';
      case 'Student Request': return '#cce5ff';
      case 'Administrative': return '#e2e3e5';
      default: return '#f8d7da';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'Course Completion': return '#155724';
      case 'Schedule Change': return '#856404';
      case 'Student Request': return '#004085';
      case 'Administrative': return '#383d41';
      default: return '#721c24';
    }
  }};
`;

const TransferFlow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const CourseInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CourseName = styled.div`
  font-weight: 600;
  color: #2c3e50;
`;

const SessionName = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
`;

const ArrowIcon = styled(FaArrowRight)`
  color: #007bff;
  font-size: 1.2rem;
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #6c757d;
`;

const EmptyStateIcon = styled(FaHistory)`
  font-size: 4rem;
  color: #dee2e6;
  margin-bottom: 20px;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-size: 1.1rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const PaginationInfo = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #007bff;
    color: #007bff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TransferHistory = () => {
  const { courses } = useSelector(state => state.masterData);
  
  // Filters
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  
  // Data
  const [transferHistory, setTransferHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  // Fetch transfer history
  const fetchHistory = async (resetOffset = false) => {
    setLoading(true);
    try {
      const filters = {
        studentSearch: studentSearch || undefined,
        courseId: selectedCourseId || undefined,
        limit: pagination.limit,
        offset: resetOffset ? 0 : pagination.offset
      };

      const response = await fetchTransferHistory(filters);
      setTransferHistory(response.data || []);
      setPagination({
        ...pagination,
        total: response.pagination.total,
        offset: resetOffset ? 0 : pagination.offset,
        hasMore: response.pagination.hasMore
      });
    } catch (error) {
      showError(error.message || 'Failed to fetch transfer history');
      setTransferHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchHistory(true);
  }, []);

  // Auto-search when filters change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchHistory(true);
    }, 500); // 500ms debounce for text input

    return () => clearTimeout(timer);
  }, [studentSearch, selectedCourseId]);

  const handleNextPage = () => {
    setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    setTimeout(() => fetchHistory(false), 0);
  };

  const handlePrevPage = () => {
    setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    setTimeout(() => fetchHistory(false), 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy hh:mm a');
    } catch {
      return dateString;
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          <FaHistory />
          Student Transfer History
        </Title>
        <Subtitle>
          View complete audit trail of all student transfers between courses and sessions
        </Subtitle>
      </Header>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <FaFilter />
          <h3 style={{ margin: 0, color: '#2c3e50' }}>Filters</h3>
        </div>

        <FilterSection>
          <FormGroup>
            <Label>Search Student</Label>
            <Input
              type="text"
              placeholder="Search by student name or email..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Course</Label>
            <Select
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
            >
              <option value="">All Courses</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.course_name}
                </option>
              ))}
            </Select>
          </FormGroup>
        </FilterSection>
      </Card>

      <Card>
        {loading && transferHistory.length === 0 ? (
          <LoadingSpinner>Loading transfer history...</LoadingSpinner>
        ) : transferHistory.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon />
            <h3>No Transfer Records Found</h3>
            <p>No student transfers match your current filters.</p>
          </EmptyState>
        ) : (
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <Table>
              <colgroup>
                <col style={{ width: '25%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '40%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '35%' }} />
              </colgroup>
              <thead>
                <tr>
                  <Th>Date & Time</Th>
                  <Th>Student</Th>
                  <Th>Transfer Details</Th>
                  <Th>Reason</Th>
                  <Th>Notes</Th>
                </tr>
              </thead>
              <tbody>
                {transferHistory.map((record) => (
                  <Tr key={record.id}>
                    <Td>{formatDate(record.transfer_date)}</Td>
                    <Td>
                      <div style={{ fontWeight: 600 }}>{record.student_name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        {record.student_email}
                      </div>
                    </Td>
                    <Td>
                      <TransferFlow>
                        <CourseInfo>
                          <CourseName>{record.old_course_name || 'New Enrollment'}</CourseName>
                          <SessionName>{record.old_session_name || '-'}</SessionName>
                        </CourseInfo>
                        <ArrowIcon />
                        <CourseInfo>
                          <CourseName>{record.new_course_name}</CourseName>
                          <SessionName>{record.new_session_name}</SessionName>
                        </CourseInfo>
                      </TransferFlow>
                    </Td>
                    <Td>
                      {record.transfer_reason ? (
                        <Badge type={record.transfer_reason}>{record.transfer_reason}</Badge>
                      ) : (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>Not specified</span>
                      )}
                    </Td>
                    <Td>
                      {record.transfer_notes ? (
                        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {record.transfer_notes}
                        </div>
                      ) : (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>-</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>

            <PaginationContainer>
              <PaginationInfo>
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} records
              </PaginationInfo>
              <PaginationButtons>
                <PageButton
                  onClick={handlePrevPage}
                  disabled={pagination.offset === 0 || loading}
                >
                  Previous
                </PageButton>
                <PageButton
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore || loading}
                >
                  Next
                </PageButton>
              </PaginationButtons>
            </PaginationContainer>
          </div>
        )}
      </Card>
    </Container>
  );
};

export default TransferHistory;

